import { useCallback } from 'react';
import { FormInstance } from 'antd/es/form';
import { notification } from 'antd';
import { TreeNode } from '../../types/types';
import { updatePageDetails } from '../../services/pageservice';

interface UsePageDetailsFormProps {
    form: FormInstance;
    data: TreeNode[];
    setData: (data: TreeNode[]) => void;
    selectedKey: string | null;
    setSelectedPageName: (name: string) => void;
    menuItemImages: any[];
    setMenuItemImagesOptions: (images: any[]) => void;
    imageUrl: string;
    setImageUrl: (url: string) => void;
    isOverrideImageUrl: boolean;
    setIsOverrideImageUrl: (value: boolean) => void;
    setIsMenuItem: (value: boolean) => void;
    setLoadingPageDetails: (loading: boolean) => void;
    handleUnsavedChanges: (hasChanges: boolean) => void;
}

export const usePageDetailsForm = ({
    form,
    data,
    setData,
    selectedKey,
    setSelectedPageName,
    menuItemImages,
    setMenuItemImagesOptions,
    imageUrl,
    setImageUrl,
    isOverrideImageUrl,
    setIsOverrideImageUrl,
    setIsMenuItem,
    setLoadingPageDetails,
    handleUnsavedChanges,
}: UsePageDetailsFormProps) => {
    const handleOk = useCallback(() => {
        form.validateFields()
            .then(values => {
                setLoadingPageDetails(true);
                const selectedImageUrl = values.selectedImageUrl || imageUrl;
                if (values.menuItem === '(SELECT)') {
                    values.menuItem = null;
                }

                if (!selectedKey) return;


                const isHeroItem: boolean = !!values.isHeroItem;
                const bannerImageUrl: string | null = isHeroItem
                    ? null
                    : (values.bannerImage?.trim() || null);
                const heroBadgeText = isHeroItem ? (values.heroBadgeText?.trim() || "FEATURED") : null;
                const heroBadgeStyle = isHeroItem ? (values.heroBadgeStyle || "orange") : null;

                const startTimeStr = values.startTime && typeof values.startTime.format === 'function'
                    ? values.startTime.format("HH:mm:ss")
                    : null;
                const endTimeStr = values.endTime && typeof values.endTime.format === 'function'
                    ? values.endTime.format("HH:mm:ss")
                    : null;

                updatePageDetails({
                    Guid: selectedKey,
                    Name: values.name,
                    MenuGuid: values.menuItem,
                    ImageName: selectedImageUrl,
                    SignatureItem: !!values.signatureItem,
                    IsHeroItem: isHeroItem,
                    BannerImageUrl: bannerImageUrl,
                    HeroBadgeText: heroBadgeText,
                    HeroBadgeStyle: heroBadgeStyle,
                    StartTime: startTimeStr,
                    EndTime: endTimeStr,
                })
                    .then(() => {
                        const newData = [...data];
                        const editNodeRecursively = (nodes: TreeNode[]) => {
                            nodes.forEach((node) => {
                                if (node.key === selectedKey) {
                                    node.title = values.name;
                                    node.imageUrl = selectedImageUrl;
                                    setSelectedPageName(values.name);
                                } else if (node.children) {
                                    editNodeRecursively(node.children);
                                }
                            });
                        };
                        editNodeRecursively(newData);
                        setData(newData);

                        const newMenuItemImages = [...menuItemImages];
                        const itemIndex = newMenuItemImages.findIndex(item => item.Guid === selectedKey);
                        if (itemIndex > -1) {
                            newMenuItemImages[itemIndex] = {
                                ...newMenuItemImages[itemIndex],
                                SignatureItem: values.signatureItem,
                            };
                        }
                        setMenuItemImagesOptions(newMenuItemImages);

                        if (isOverrideImageUrl) {
                            setImageUrl(selectedImageUrl);
                        }

                        handleUnsavedChanges(false);
                        notification.success({
                            message: 'Page Updated',
                            description: 'Page details were successfully saved.',
                            placement: 'bottomLeft',
                        });
                    })
                    .catch(error => {
                        console.error('Error updating page:', error);
                        notification.error({
                            message: 'Update Failed',
                            description: 'There was an error updating the page.',
                            placement: 'bottomLeft',
                        });
                    })
                    .finally(() => {
                        setLoadingPageDetails(false);
                    });
            })
            .catch(info => {
                console.error('Validate Failed:', info);
            });
    }, [
        form, data, selectedKey, imageUrl, isOverrideImageUrl,
        setLoadingPageDetails, setImageUrl, setIsOverrideImageUrl,
        setData, setSelectedPageName, setMenuItemImagesOptions,
        menuItemImages, handleUnsavedChanges
    ]);

    const handleDropdownSelection = useCallback((guid: string, option: any) => {
        const selectedImageUrl = option.dataImageUrl;
        const selectedName = option.children;
        setImageUrl(selectedImageUrl);
        form.setFieldsValue({
            selectedImageUrl,
            signatureItem: option.dataSignatureItem,
            name: selectedName,
        });
    }, [form, setImageUrl]);

    const handleMenuItemChange = useCallback((value: boolean) => {
        setIsMenuItem(value);
        if (!value) {
            form.setFieldsValue({
                source: null,
                menuItem: null,
                selectedImageUrl: null,
            });
            setImageUrl('');
        }
    }, [form, setImageUrl, setIsMenuItem]);

    const handleImageUrlChange = useCallback((value: boolean) => {
        setIsOverrideImageUrl(value);
    }, [setIsOverrideImageUrl]);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setImageUrl(e.target.value);
    }, [setImageUrl]);

    return {
        handleOk,
        handleDropdownSelection,
        handleMenuItemChange,
        handleImageUrlChange,
        handleInputChange,
    };
};